/**
 * `GET /v1/home` — a Home composta pelo SERVIDOR (`CONTRACTS §8.2` bloco "Catalogo e busca",
 * `API §6.20.4`, `§8.1`; §7 e §43 do briefing).
 *
 * Fontes: `API §6.20.4` (a forma exata da resposta e as duas regras de degradacao), `§8.1`
 * (as duas linhas de cache desta rota), `DATA_MODEL §3.11` (`home_section`, PREVISTA) e a
 * decisao **C-04** ("tipo de widget e FECHADO — codigo; instancia de secao e dado — backoffice,
 * sem deploy").
 *
 * ## Tres coisas que este arquivo DECIDE, e nao apenas descreve
 *
 * **1. A uniao de secoes e FECHADA, e isso nao contradiz o §43.** `API §6.20.4` fixa a regra
 * "o cliente IGNORA silenciosamente `type` desconhecido -> secao nova sem deploy do PWA", e e
 * facil ler dai que o contrato deveria aceitar qualquer `type`. Nao deveria: **C-04** separa as
 * duas coisas com todas as letras — o TIPO de widget e fechado em codigo, a INSTANCIA de secao
 * e dado. O que o §43 compra e publicar uma campanha nova sem deploy; o que ele nunca prometeu
 * e um tipo de widget que ninguem desenhou. A uniao fechada aqui e, por consequencia, a ultima
 * linha de defesa da rota: uma secao que o servidor monte errado nao atravessa o `parse` da
 * propria resposta. A promessa de compatibilidade e do CLIENTE, que ja a implementa por conta
 * propria (`web-cliente/src/app/home.ts` casa por `type` e ignora o que nao conhece).
 *
 * **2. So os tres tipos que o MVP SERVE tem schema.** `API §6.20.4` nomeia nove
 * (`hero`, `categories`, `campaign`, `personalized_offers`, `buy_again`, `medications`,
 * `loyalty`, `nearby_store`, `health_services`). Seis deles dependem de tipos que **nao existem
 * em documento nenhum** (`Banner`, `MedicationReminderCard`, `HealthServiceCard`) ou de tabelas
 * fora do MVP (`loyalty_*`, `prescription`, `campaign`/`banner` — `DATA_MODEL §3.12`).
 * Escrever schema para eles seria inventar contrato, que e o que `CONTRACTS §8` proibe. Os nove
 * nomes ficam registrados em `HOME_SECTION_TYPE_VALUES` — a lista fechada de C-04 —, e cada um
 * ganha schema no PR que o traz.
 *
 * **3. O `Cache-Control` desta rota sao DUAS constantes, e nao uma.** `API §8.1` da uma linha
 * por estado de sessao: anonimo e cacheavel na borda por 2 min, autenticado e `no-store` com
 * `Vary: Cookie` (premissa P3 do `PWA.md`, X-05). E a unica rota publica deste pacote em que o
 * cabecalho depende de quem pergunta, e por isso as duas moram aqui, nomeadas, em vez de
 * virarem um `if` com literais dentro de um controller.
 */
import { isMedication } from '@farmacia/domain';
import { z } from 'zod';

import { InstantSchema, SlugSchema, Uuidv7Schema } from '../common';
import { ProductListItemSchema } from './catalog';

// ---------------------------------------------------------------------------
// Os tipos de secao — a lista fechada de C-04
// ---------------------------------------------------------------------------

/**
 * Os nove tipos de widget de `API §6.20.4`. **Fechado em codigo (C-04)**, e registrado aqui
 * inteiro mesmo com seis deles ainda sem schema: a lista e o que torna verificavel a afirmacao
 * "tipo novo e PR de contrato, nao configuracao de instancia".
 *
 * O que o servidor efetivamente monta hoje esta em `HOME_SERVED_SECTION_TYPES`, que e um
 * subconjunto proprio deste — e `home.test.ts` prova que e subconjunto, para que ninguem sirva
 * um tipo que a lista fechada nao contem.
 */
export const HOME_SECTION_TYPE_VALUES = [
  'hero',
  'categories',
  'campaign',
  'personalized_offers',
  'buy_again',
  'medications',
  'loyalty',
  'nearby_store',
  'health_services',
] as const;
export type HomeSectionType = (typeof HOME_SECTION_TYPE_VALUES)[number];

/** Os tres que o MVP serve. Ver a decisao 2 no topo do arquivo. */
export const HOME_SERVED_SECTION_TYPES = ['categories', 'campaign', 'personalized_offers'] as const;
export type HomeServedSectionType = (typeof HOME_SERVED_SECTION_TYPES)[number];

// ---------------------------------------------------------------------------
// `categories` — os chips de navegacao
// ---------------------------------------------------------------------------

/**
 * Um chip de categoria (`CategoryChip` de `API §6.20.4`).
 *
 * E MENOR que `CategoryNode` de `catalog.ts` de proposito: o chip navega, e para navegar bastam
 * rotulo e destino. `path`, `depth`, `parentId` e `isTherapeutic` existem para o cliente MONTAR
 * a arvore em `/v1/catalog/categories`; repeti-los na Home custaria bytes em 3G para reconstruir
 * uma hierarquia que a Home nao desenha.
 *
 * `isTherapeutic` fica de fora tambem por REG-044, e a leitura merece a nota: a regra proibe
 * usar categoria terapeutica em **segmentacao de marketing**, nao em navegacao. Os chips
 * continuam podendo apontar para "Medicamentos"; o que nao pode e a categoria virar criterio de
 * publico-alvo. Como a Home nao segmenta ninguem nesta rodada, o campo simplesmente nao serve
 * para nada aqui — e campo que nao serve nao viaja.
 */
export const CategoryChipSchema = z.object({
  id: Uuidv7Schema,
  slug: SlugSchema,
  /** O texto do chip (`category.name`). Nunca vazio: chip sem rotulo nao e alvo de toque. */
  label: z.string().min(1),
  iconKey: z.string().min(1).nullable(),
});
export type CategoryChip = z.infer<typeof CategoryChipSchema>;

export const HomeCategoriesSectionSchema = z.object({
  type: z.literal('categories'),
  id: z.string().min(1),
  items: z.array(CategoryChipSchema),
});
export type HomeCategoriesSection = z.infer<typeof HomeCategoriesSectionSchema>;

// ---------------------------------------------------------------------------
// `campaign` e `personalized_offers` — as duas prateleiras de produto
// ---------------------------------------------------------------------------

/**
 * A prateleira de destaque (`campaign` de `API §6.20.4`).
 *
 * **`mandatoryWarning` nao e opcional, e por isso e `nullable` e nao `optional`.** REG-035 exige
 * a advertencia em toda peca de MIP, e uma peca e exatamente o que esta secao e: produto em
 * destaque, escolhido pela loja. O campo obriga quem monta a secao a responder "ha MIP aqui?"
 * — `null` e a resposta "nao ha", declarada; a ausencia do campo seria a pergunta nao feita.
 * `home.test.ts` prova o par: item MIP na secao com `mandatoryWarning: null` **nao passa**.
 *
 * O item e o `ProductListItem` de `catalog.ts`, e nao um item proprio da Home. Isso importa
 * mais do que parece: o mesmo schema carrega `regulatory` (com a advertencia por item) e
 * `availability` (faixa, nunca contagem — RN-063), entao a Home herda os dois gates estruturais
 * da vitrine sem que ninguem precise lembrar deles aqui.
 */
export const HomeCampaignSectionSchema = z
  .object({
    type: z.literal('campaign'),
    id: z.string().min(1),
    title: z.string().min(1),
    /** REG-035 — a frase de advertencia, quando a prateleira toca MIP. `null` quando nao toca. */
    mandatoryWarning: z.string().min(1).nullable(),
    items: z.array(ProductListItemSchema),
  })
  .superRefine((secao, ctx) => {
    // O gate estrutural de REG-035: se algum item da peca e MIP, a peca TEM de trazer a
    // advertencia. A regra de qual e a frase e de `@farmacia/domain` (REG-011 — `policy.ts` e o
    // unico lugar que compara classe); o que se verifica aqui e que ela nao ficou de fora.
    const tocaMip = secao.items.some((item) => item.regulatory.class === 'MIP');
    if (tocaMip && secao.mandatoryWarning === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['mandatoryWarning'],
        message:
          'REG-035: peca de campanha com MIP exige a advertencia obrigatoria; `null` nao e resposta valida aqui.',
      });
    }
  });
export type HomeCampaignSection = z.infer<typeof HomeCampaignSectionSchema>;

/**
 * A prateleira personalizada (`personalized_offers` de `API §6.20.4`).
 *
 * `API §6.20.4` escreve, ao lado do tipo: *"ja filtrado: `canBePromoted && !medicamento`
 * (REG-037)"*, e *"chega vazia (nao ausente) quando nao ha base legal — a UI colapsa a secao"*.
 * As duas frases sao contrato:
 *
 *  - o filtro e do servidor, e o schema o torna verificavel: `refine` recusa medicamento e
 *    recusa item com `canBePromoted: false`. Uma oferta personalizada de medicamento e
 *    publicidade dirigida de medicamento, que e o que REG-037 fecha;
 *  - `items: []` e um estado **valido e esperado**, nunca um erro nem uma secao ausente.
 */
export const HomePersonalizedOffersSectionSchema = z
  .object({
    type: z.literal('personalized_offers'),
    id: z.string().min(1),
    title: z.string().min(1),
    items: z.array(ProductListItemSchema),
  })
  .superRefine((secao, ctx) => {
    for (const [indice, item] of secao.items.entries()) {
      if (!item.regulatory.canBePromoted) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['items', indice, 'regulatory', 'canBePromoted'],
          message: 'REG-037: oferta personalizada so aceita item que pode ser anunciado.',
        });
      }
      // "!medicamento" da nota de `API §6.20.4`. A pergunta "esta classe e medicamento?" NAO e
      // redigitada aqui: quem responde e `isMedication` de `@farmacia/domain`, que REG-011
      // define como fonte unica. O contrato so a consulta — e por isso uma 17a classe de
      // medicamento passa a ser recusada nesta secao sem que ninguem edite este arquivo.
      if (isMedication(item.regulatory.class)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['items', indice, 'regulatory', 'class'],
          message: 'REG-037: oferta personalizada nunca carrega medicamento.',
        });
      }
    }
  });
export type HomePersonalizedOffersSection = z.infer<typeof HomePersonalizedOffersSectionSchema>;

/**
 * A secao, como ela viaja.
 *
 * `z.union` e nao `z.discriminatedUnion` por uma razao de ferramenta, nao de desenho: duas das
 * tres alternativas sao `ZodEffects` (tem `superRefine`), e `discriminatedUnion` exige objetos
 * crus. A discriminacao continua existindo — `type` e literal em cada ramo —, e o custo e uma
 * mensagem de erro mais longa quando o `parse` falha, que e um problema de quem esta depurando
 * o servidor, nao do cliente.
 */
export const HomeSectionSchema = z.union([
  HomeCategoriesSectionSchema,
  HomeCampaignSectionSchema,
  HomePersonalizedOffersSectionSchema,
]);
export type HomeSection = z.infer<typeof HomeSectionSchema>;

// ---------------------------------------------------------------------------
// A resposta
// ---------------------------------------------------------------------------

/**
 * O estado da filial no topo da Home (`API §6.20.4`).
 *
 * E deliberadamente MENOR que `StoreOpenStatus` de `stores.ts`, que tem oito campos. `§6.20.4`
 * pede tres, e a Home usa exatamente tres: se da para pedir agora, ate quando esta pausada, e
 * a faixa de prazo. Repetir `closedReason`, `opensAt` e `onCall` aqui criaria uma segunda
 * resposta para "a loja esta aberta?" — a rota que responde isso e `GET /v1/stores/{slug}`.
 *
 * `etaBand` e `string | null` (e nao o `EtaBand` de `checkout.ts`, que e um objeto): `§6.20.4`
 * o tipa assim, e o que a Home mostra e um rotulo pronto ("30 a 50 minutos"), nunca dois
 * numeros que o cliente teria de formatar. A faixa e sempre FAIXA — R-10 e `DESIGN_SYSTEM §9.4`
 * proibem minuto exato.
 */
export const HomeStoreStatusSchema = z.object({
  open: z.boolean(),
  /** Rotulo de faixa de prazo, ja formatado. `null` quando nao ha zona/prazo resolvido. */
  etaBand: z.string().min(1).nullable(),
  pausedUntil: InstantSchema.nullable(),
});
export type HomeStoreStatus = z.infer<typeof HomeStoreStatusSchema>;

/**
 * `GET /v1/home` (`API §6.20.4`).
 *
 * **A ordem de `sections` e do SERVIDOR, e isso e contrato.** O cliente renderiza na ordem
 * recebida e nao reordena — e a metade da promessa do §43 que nao depende de tabela nenhuma:
 * mudar a ordem das faixas e mudar a resposta, nunca o app instalado.
 *
 * `sections: []` e resposta valida. Nao ha `204`, nao ha `404` e nao ha erro para "esta filial
 * nao tem nada a mostrar hoje": a Home vazia e um estado do produto, e o frontend ja a trata
 * colapsando toda secao sem conteudo.
 */
export const HomeResponseSchema = z.object({
  /**
   * Muda quando a composicao muda; entra no `ETag` e na chave de cache (`API §6.20.4`).
   *
   * Nao e opaco por acaso — e `string`, e nao um numero de versao de tabela, justamente para
   * que a fonte da composicao possa trocar (codigo hoje, `home_section` amanha) sem mudar o
   * contrato. Quem consome so precisa saber que valores diferentes significam layouts
   * diferentes.
   */
  layoutVersion: z.string().min(1),
  storeId: Uuidv7Schema,
  storeStatus: HomeStoreStatusSchema,
  sections: z.array(HomeSectionSchema),
});
export type HomeResponse = z.infer<typeof HomeResponseSchema>;

/**
 * `?storeId=` — **obrigatorio**, pelo mesmo motivo de `GET /v1/catalog/products`: preco,
 * disponibilidade e estado de loja nao existem fora de uma filial. Ausente ⇒
 * `422 VALIDATION_ERROR`, nunca uma Home com produto de uma filial arbitraria.
 */
export const HomeQuerySchema = z.object({ storeId: Uuidv7Schema });
export type HomeQuery = z.infer<typeof HomeQuerySchema>;

// ---------------------------------------------------------------------------
// Cache — API §8.1, uma constante por estado de sessao
// ---------------------------------------------------------------------------

/**
 * `GET /v1/home` **anonimo** — `API §8.1`: `public, s-maxage=120, swr=600`, chaveado por
 * `(storeId, layoutVersion)`.
 *
 * Dois minutos e curto para uma vitrine e proposital: a Home carrega preco e faixa de
 * disponibilidade, e `API §8.1` nunca deixa estoque passar de dois digitos de segundos na
 * borda. O `swr` de 10 min e o que mantem a abertura do app rapida quando a origem esta lenta.
 */
export const HOME_ANONYMOUS_CACHE_CONTROL = 'public, s-maxage=120, stale-while-revalidate=600';

/**
 * `GET /v1/home` **autenticado** — `private, no-store`, com `Vary: Cookie`.
 *
 * A regra e mais forte que "nao cacheie o bloco pessoal": e a premissa P3 do `PWA.md`, na
 * correcao X-05 — **todo endpoint autenticado responde `no-store`**. `API §8.1` registra que
 * `ETag`/`If-None-Match` continua funcionando com `no-store`, e por isso a rota emite `ETag`
 * mesmo aqui: revalidar e barato na abertura do app, e `no-store` proibe guardar, nao
 * comparar.
 */
export const HOME_AUTHENTICATED_CACHE_CONTROL = 'private, no-store';
